import React from 'react';
import { createRoot } from 'react-dom/client';
import { requestAccess, signTransaction } from '@stellar/freighter-api';
import {
  AlertTriangle,
  ArrowRight,
  ChevronDown,
  CheckCircle2,
  ClipboardCheck,
  Copy,
  Cpu,
  Database,
  Download,
  FileCheck2,
  HelpCircle,
  KeyRound,
  Network,
  RadioTower,
  ServerOff,
  ShieldCheck,
  Terminal,
  Upload,
  X,
} from 'lucide-react';
import {
  buildFixtureFromEditorText,
  buildFixtureFromPayloadText,
  buildFixtureFromUnknown,
  DemoFixture,
  fixtureToEditorText,
  FixtureEditorText,
  Snapshot,
} from './demoData';
import { buildFixtureFromCsv } from './csvProof';
import {
  RpcPublishForm,
  defaultRpcPublishForm,
  loadDemoOnchainSetup as fetchDemoOnchainSetup,
  publishReserveSnapshotViaRpc,
  requestWalletAirdrop,
} from './stellarRpc';
import { Check, runVerification } from './verification';
import './styles.css';

const LIABILITY_CSV_TEMPLATE = `username,balance
alice,40000
bob,25000
carol,15000
dave,4359
`;

const EMPTY_EDITOR_TEXT: FixtureEditorText = {
  snapshot: '',
  report: '',
  proof: '',
};

const STELLAR_TESTNET_TX_BASE_URL = 'https://stellar.expert/explorer/testnet/tx/';

type IssuerCsvForm = {
  assets: string;
  attestor: string;
  period: string;
  proofIndex: string;
  subject: string;
};

type GuideStepDefinition = {
  target: string;
  title: string;
  detail: string;
  hint: string;
};

type GuideSpotlight = {
  top: number;
  left: number;
  width: number;
  height: number;
};

const GUIDE_STEPS: GuideStepDefinition[] = [
  {
    target: 'download-csv',
    title: 'Download the CSV template',
    detail:
      'Start here. The CSV has the exact username,balance headers the issuer workflow expects.',
    hint: 'Click Download CSV, then use that file as your liabilities input.',
  },
  {
    target: 'load-csv',
    title: 'Load or paste liabilities',
    detail:
      'Import the CSV file, or paste rows directly into the CSV box below this button.',
    hint: 'All CSV parsing happens locally in the browser.',
  },
  {
    target: 'generate-proof',
    title: 'Generate the proof package',
    detail:
      'After entering assets, period, proof row, subject, and attestor, this builds the local proof package.',
    hint: 'The generated package includes the snapshot, report, and proof fields.',
  },
  {
    target: 'verify-local',
    title: 'Verify locally first',
    detail:
      'This checks the proof package, hashes, circuit id, and solvency threshold before anything touches testnet.',
    hint: 'You should see Local proof verified before continuing.',
  },
  {
    target: 'verify-onchain',
    title: 'Prepare on-chain verification',
    detail:
      'This confirms the local proof is ready for the publish workflow and points you to the Publish section.',
    hint: 'It does not send a transaction by itself.',
  },
  {
    target: 'load-onchain-setup',
    title: 'Load the demo on-chain setup',
    detail:
      'This fetches the registry contract, verifier contract, verification key, proof, and public signals from public JSON files.',
    hint: 'The JSON is served as assets, not hardcoded in the app bundle.',
  },
  {
    target: 'publish-rpc',
    title: 'Publish through Stellar RPC',
    detail:
      'This asks the connected browser wallet to sign, then submits the attestation. The registry calls the Groth16 verifier on-chain.',
    hint: 'A successful publish returns a Stellar transaction hash.',
  },
  {
    target: 'wallet-menu',
    title: 'Connect your browser wallet',
    detail:
      'Use the wallet dropdown to connect Freighter, copy the address, or fund the connected testnet account.',
    hint: 'The app never sees or stores your secret key.',
  },
];

function App() {
  const path = window.location.pathname.replace(/\/+$/, '') || '/';

  return path === '/demo' ? <Demo /> : <Launch />;
}

function Launch() {
  return (
    <main className="launchShell">
      <header className="launchNav">
        <a className="brandLockup" href="/">
          <span className="brandMark">
            <ShieldCheck size={24} />
          </span>
          <span>zkreserves</span>
        </a>
        <nav className="topTabs" aria-label="App sections">
          <a className="topTab" href="/">
            Home
          </a>
          <a className="topTab active" href="/demo#submit">
            Issuer
          </a>
          <a className="topTab" href="/demo#verify">
            Verify
          </a>
          <a className="topTab" href="/demo#publish">
            Publish
          </a>
        </nav>
      </header>

      <section className="launchHero">
        <div className="heroCopy">
          <p className="eyebrow">Stellar reserve attestation</p>
          <h1>zkreserves</h1>
          <p>
            Verify reserve snapshots in the browser, then publish attestations through a Soroban
            registry that checks the Groth16 proof on-chain before accepting the record.
          </p>
          <div className="heroActions">
            <a className="primaryAction" href="/demo">
              <ArrowRight size={18} />
              Launch app
            </a>
            <a className="secondaryAction" href="/demo#submit">
              <Database size={18} />
              Try issuer workflow
            </a>
          </div>
        </div>

        <div className="proofVisual" aria-label="Reserve proof flow">
          <div className="proofNode proofNodeLarge">
            <ShieldCheck size={36} />
            <span>Proof</span>
          </div>
          <div className="proofRail">
            <span />
            <span />
            <span />
          </div>
          <div className="proofNode">
            <Cpu size={26} />
            <span>Local</span>
          </div>
          <div className="proofNode">
            <Network size={26} />
            <span>On-chain</span>
          </div>
        </div>
      </section>

      <section className="launchGrid" aria-label="Verification path">
        <Feature icon={<ServerOff size={20} />} title="Local preview">
          Processing happens locally. Nothing gets sent to a server from the demo verifier.
        </Feature>
        <Feature icon={<KeyRound size={20} />} title="On-chain gate">
          The registry publish path calls a Groth16 verifier contract before it stores a snapshot.
        </Feature>
        <Feature icon={<FileCheck2 size={20} />} title="Stellar record">
          Accepted attestations anchor the public reserve snapshot, proof hash, and report hash.
        </Feature>
      </section>
    </main>
  );
}

function Demo() {
  const [fixture, setFixture] = React.useState<DemoFixture | null>(null);
  const [editorText, setEditorText] = React.useState<FixtureEditorText>(EMPTY_EDITOR_TEXT);
  const [checks, setChecks] = React.useState<Check[]>([]);
  const [copied, setCopied] = React.useState(false);
  const [csvFileName, setCsvFileName] = React.useState('No CSV loaded');
  const [csvText, setCsvText] = React.useState('');
  const [generatedFixture, setGeneratedFixture] = React.useState<DemoFixture | null>(null);
  const [isGenerating, setIsGenerating] = React.useState(false);
  const [inputError, setInputError] = React.useState('');
  const [localVerificationStatus, setLocalVerificationStatus] = React.useState('');
  const [onchainVerificationStatus, setOnchainVerificationStatus] = React.useState('');
  const [rpcForm, setRpcForm] = React.useState<RpcPublishForm>(() => defaultRpcPublishForm());
  const [rpcStatus, setRpcStatus] = React.useState('');
  const [rpcError, setRpcError] = React.useState('');
  const [rpcTransactionHash, setRpcTransactionHash] = React.useState('');
  const [isFundingWallet, setIsFundingWallet] = React.useState(false);
  const [walletMenuOpen, setWalletMenuOpen] = React.useState(false);
  const [copiedWallet, setCopiedWallet] = React.useState(false);
  const [walletAddress, setWalletAddress] = React.useState('');
  const [isPublishingRpc, setIsPublishingRpc] = React.useState(false);
  const [guideOpen, setGuideOpen] = React.useState(true);
  const [guideStepIndex, setGuideStepIndex] = React.useState(0);
  const [guideSpotlight, setGuideSpotlight] = React.useState<GuideSpotlight | null>(null);
  const [issuerCsvForm, setIssuerCsvForm] = React.useState<IssuerCsvForm>({
    assets: '',
    attestor: '',
    period: '',
    proofIndex: '',
    subject: '',
  });

  React.useEffect(() => {
    let cancelled = false;
    setChecks([]);

    if (!fixture) {
      return () => {
        cancelled = true;
      };
    }

    runVerification(fixture.snapshot, fixture.report, fixture.proof)
      .then((nextChecks) => {
        if (!cancelled) {
          setChecks(nextChecks);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          const detail = error instanceof Error ? error.message : 'Unable to verify input';
          setChecks([{ label: 'Verifier input', detail, passed: false }]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [fixture]);

  const passed = Boolean(fixture) && checks.length > 0 && checks.every((check) => check.passed);
  const coverage = fixture ? formatCoverage(fixture.snapshot) : 'n/a';
  const statusText = fixture
    ? passed
      ? 'Verified locally'
      : checks.length
        ? 'Issues detected'
        : 'Verifying'
    : 'No proof submitted';
  const submittedGeneratedProof = Boolean(generatedFixture && fixture?.id === generatedFixture.id);
  const activeGuideStep = GUIDE_STEPS[guideStepIndex];

  React.useEffect(() => {
    if (!guideOpen) {
      return undefined;
    }

    function closeOnEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setGuideOpen(false);
      }
    }

    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [guideOpen]);

  React.useEffect(() => {
    if (!guideOpen) {
      setGuideSpotlight(null);
      return undefined;
    }

    let cancelled = false;
    const selector = `[data-guide-id="${activeGuideStep.target}"]`;

    function updateSpotlight() {
      const target = document.querySelector<HTMLElement>(selector);
      if (!target || cancelled) {
        setGuideSpotlight(null);
        return;
      }

      const rect = target.getBoundingClientRect();
      setGuideSpotlight({
        top: Math.max(8, rect.top - 8),
        left: Math.max(8, rect.left - 8),
        width: Math.min(window.innerWidth - 16, rect.width + 16),
        height: rect.height + 16,
      });
    }

    const target = document.querySelector<HTMLElement>(selector);
    target?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
    updateSpotlight();

    const scrollTimer = window.setTimeout(updateSpotlight, 320);
    window.addEventListener('resize', updateSpotlight);
    window.addEventListener('scroll', updateSpotlight, true);

    return () => {
      cancelled = true;
      window.clearTimeout(scrollTimer);
      window.removeEventListener('resize', updateSpotlight);
      window.removeEventListener('scroll', updateSpotlight, true);
    };
  }, [activeGuideStep.target, guideOpen]);

  function openGuide() {
    setGuideStepIndex(0);
    setGuideOpen(true);
  }

  function closeGuide() {
    setGuideOpen(false);
  }

  function advanceGuide() {
    if (guideStepIndex === GUIDE_STEPS.length - 1) {
      closeGuide();
      return;
    }
    setGuideStepIndex((current) => current + 1);
  }

  function reverseGuide() {
    setGuideStepIndex((current) => Math.max(0, current - 1));
  }

  function applyFixture(nextFixture: DemoFixture) {
    setFixture(nextFixture);
    setEditorText(fixtureToEditorText(nextFixture));
    setInputError('');
  }

  async function copyPayload() {
    if (!fixture) {
      return;
    }
    await navigator.clipboard.writeText(JSON.stringify(fixture, null, 2));
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1600);
  }

  function downloadAssets() {
    downloadTextAsset(LIABILITY_CSV_TEMPLATE, 'zkreserves-liabilities-template.csv', 'text/csv');
  }

  async function loadCsvFile(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.currentTarget.files?.[0];
    event.currentTarget.value = '';
    if (!file) {
      return;
    }

    try {
      setCsvText(await file.text());
      setCsvFileName(file.name);
      setGeneratedFixture(null);
      setLocalVerificationStatus('');
      setOnchainVerificationStatus('');
      setInputError('');
    } catch {
      setInputError('Unable to read CSV file');
    }
  }

  async function generateCsvProof() {
    try {
      setIsGenerating(true);
      setInputError('');
      const proofIndex = Number(issuerCsvForm.proofIndex);
      if (!Number.isInteger(proofIndex) || proofIndex < 0) {
        throw new Error('Proof row must be a non-negative integer');
      }

      const nextFixture = await buildFixtureFromCsv(csvText, {
        assets: issuerCsvForm.assets,
        attestor: issuerCsvForm.attestor,
        fileName: csvFileName,
        period: issuerCsvForm.period,
        proofIndex,
        subject: issuerCsvForm.subject,
      });

      setGeneratedFixture(nextFixture);
      setEditorText(fixtureToEditorText(nextFixture));
      setFixture(null);
      setChecks([]);
      setLocalVerificationStatus('');
      setOnchainVerificationStatus('');
    } catch (error) {
      setGeneratedFixture(null);
      setInputError(error instanceof Error ? error.message : 'Unable to generate proof package');
    } finally {
      setIsGenerating(false);
    }
  }

  async function verifyGeneratedProofLocally() {
    if (!generatedFixture) {
      setInputError('Generate a proof package before verifying it');
      return;
    }

    const nextChecks = await runVerification(
      generatedFixture.snapshot,
      generatedFixture.report,
      generatedFixture.proof,
    );
    setFixture(generatedFixture);
    setChecks(nextChecks);
    setEditorText(fixtureToEditorText(generatedFixture));
    setLocalVerificationStatus(
      nextChecks.every((check) => check.passed)
        ? 'Local proof verified'
        : 'Local proof did not pass every check',
    );
    setOnchainVerificationStatus('');
    setInputError('');
  }

  async function verifyGeneratedProofOnchain() {
    if (!generatedFixture) {
      setInputError('Generate a proof package before verifying it on-chain');
      return;
    }

    const nextChecks = await runVerification(
      generatedFixture.snapshot,
      generatedFixture.report,
      generatedFixture.proof,
    );
    setFixture(generatedFixture);
    setChecks(nextChecks);
    setEditorText(fixtureToEditorText(generatedFixture));
    setLocalVerificationStatus(
      nextChecks.every((check) => check.passed)
        ? 'Local proof verified'
        : 'Local proof did not pass every check',
    );
    setOnchainVerificationStatus(
      nextChecks.every((check) => check.passed)
        ? 'Ready to publish from the browser. Open Publish, click Load demo on-chain setup, then Publish via RPC.'
        : 'On-chain verification blocked until local checks pass',
    );
    setInputError('');
  }

  function updateIssuerCsvForm(field: keyof IssuerCsvForm, value: string) {
    setIssuerCsvForm((current) => ({ ...current, [field]: value }));
  }

  function updateRpcForm(field: keyof RpcPublishForm, value: string) {
    setRpcForm((current) => ({ ...current, [field]: value }));
    setRpcStatus('');
    setRpcError('');
    setRpcTransactionHash('');
  }

  async function loadDemoOnchainSetup() {
    try {
      setRpcStatus('Loading demo on-chain setup');
      setRpcError('');
      setRpcTransactionHash('');
      const setup = await fetchDemoOnchainSetup(rpcForm);
      setRpcForm(setup.form);
      applyFixture(buildFixtureFromUnknown(setup.fixture));
      setRpcStatus(
        'Demo on-chain setup loaded. Verification key, proof, and public signals were fetched from public demo files.',
      );
    } catch (error) {
      setRpcStatus('');
      setRpcError(error instanceof Error ? error.message : 'Unable to load demo on-chain setup');
    }
  }

  async function connectWallet() {
    try {
      setRpcStatus('Requesting browser wallet access');
      setRpcError('');
      setRpcTransactionHash('');
      const result = await requestAccess();
      assertFreighterResult(result, 'Unable to connect browser wallet');
      setWalletAddress(result.address);
      setRpcStatus(`Wallet connected: ${shortAddress(result.address)}`);
      setWalletMenuOpen(false);
    } catch (error) {
      setRpcStatus('');
      setRpcError(error instanceof Error ? error.message : 'Unable to connect browser wallet');
    }
  }

  function disconnectWallet() {
    setWalletAddress('');
    setCopiedWallet(false);
    setRpcStatus('Wallet disconnected');
    setRpcTransactionHash('');
    setWalletMenuOpen(false);
  }

  async function fundConnectedWallet() {
    if (!walletAddress) {
      setRpcError('Connect a browser wallet before funding testnet XLM');
      return;
    }

    try {
      setIsFundingWallet(true);
      setRpcStatus('Requesting testnet funds for connected wallet');
      setRpcError('');
      setRpcTransactionHash('');
      const accountId = await requestWalletAirdrop(rpcForm, walletAddress);
      setRpcStatus(`Connected wallet funded: ${shortAddress(accountId)}`);
      setWalletMenuOpen(false);
    } catch (error) {
      setRpcError(error instanceof Error ? error.message : 'Unable to fund connected wallet');
      setRpcStatus('');
    } finally {
      setIsFundingWallet(false);
    }
  }

  async function copyConnectedWallet() {
    if (!walletAddress) {
      setRpcError('Connect a browser wallet before copying an address');
      return;
    }

    await navigator.clipboard.writeText(walletAddress);
    setCopiedWallet(true);
    window.setTimeout(() => setCopiedWallet(false), 1600);
  }

  async function publishViaRpc() {
    if (!fixture) {
      setRpcError('Verify a proof package before publishing on-chain');
      return;
    }
    if (!walletAddress) {
      setRpcError('Connect a browser wallet before publishing on-chain');
      return;
    }

    try {
      setIsPublishingRpc(true);
      setRpcStatus('Submitting transaction through Stellar RPC');
      setRpcError('');
      setRpcTransactionHash('');
      const result = await publishReserveSnapshotViaRpc(
        fixture,
        rpcForm,
        walletAddress,
        signWalletTransaction,
      );
      setRpcStatus('On-chain publish succeeded');
      setRpcTransactionHash(result.hash);
    } catch (error) {
      setRpcError(error instanceof Error ? error.message : 'Unable to publish through RPC');
      setRpcStatus('');
      setRpcTransactionHash('');
    } finally {
      setIsPublishingRpc(false);
    }
  }

  function verifyEditorPayload() {
    try {
      applyFixture(buildFixtureFromEditorText(editorText));
    } catch (error) {
      setInputError(error instanceof Error ? error.message : 'Unable to parse local JSON');
    }
  }

  async function loadFixtureFile(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.currentTarget.files?.[0];
    event.currentTarget.value = '';
    if (!file) {
      return;
    }

    try {
      const text = await file.text();
      applyFixture(buildFixtureFromPayloadText(text));
    } catch (error) {
      setInputError(error instanceof Error ? error.message : 'Unable to parse fixture file');
    }
  }

  function updateEditor(field: keyof FixtureEditorText, value: string) {
    setEditorText((current) => ({ ...current, [field]: value }));
  }

  return (
    <main className="shell">
      <aside className="rail" aria-label="Status">
        <a className="brandMark" href="/" aria-label="zkreserves home">
          <ShieldCheck size={24} />
        </a>
        <span className="railDot active" />
        <span className="railDot" />
        <span className="railDot" />
      </aside>

      <section className="workspace">
        <header className="topbar">
          <div>
            <p className="eyebrow">Stellar reserve verifier</p>
            <h1>zkreserves</h1>
          </div>
          <div className="topbarActions">
            <nav className="topTabs" aria-label="Demo sections">
              <a className="topTab" href="/">
                Home
              </a>
              <a className="topTab active" href="#submit">
                Issuer
              </a>
              <a className="topTab" href="#advanced">
                Advanced
              </a>
              <a className="topTab" href="#verify">
                Verify
              </a>
              <a className="topTab" href="#publish">
                Publish
              </a>
            </nav>
            <button
              className="utilityButton"
              data-guide-id="download-csv"
              onClick={downloadAssets}
              type="button"
            >
              <Download size={16} />
              Download CSV
            </button>
            <button className="utilityButton" onClick={openGuide} type="button">
              <HelpCircle size={16} />
              Guide
            </button>
            <div className="topWallet">
              <button
                className="walletTrigger"
                aria-expanded={walletMenuOpen}
                aria-haspopup="menu"
                data-guide-id="wallet-menu"
                onClick={() => setWalletMenuOpen((open) => !open)}
                type="button"
              >
                <KeyRound size={16} />
                <span className="walletIdentity">
                  <span>Wallet</span>
                  <code>{walletAddress ? shortAddress(walletAddress) : 'Not connected'}</code>
                </span>
                <ChevronDown size={15} />
              </button>
              {walletMenuOpen ? (
                <div className="walletMenu" role="menu">
                  {walletAddress ? (
                    <>
                      <button className="walletMenuItem" onClick={copyConnectedWallet} type="button">
                        <Copy size={15} />
                        {copiedWallet ? 'Copied address' : 'Copy address'}
                      </button>
                      <button
                        className="walletMenuItem"
                        disabled={isFundingWallet}
                        onClick={fundConnectedWallet}
                        type="button"
                      >
                        <RadioTower size={15} />
                        {isFundingWallet ? 'Funding wallet' : 'Fund testnet wallet'}
                      </button>
                      <button className="walletMenuItem" onClick={disconnectWallet} type="button">
                        <X size={15} />
                        Disconnect
                      </button>
                    </>
                  ) : (
                    <button className="walletMenuItem" onClick={connectWallet} type="button">
                      <KeyRound size={15} />
                      Connect Freighter
                    </button>
                  )}
                </div>
              ) : null}
            </div>
          </div>
        </header>

        {guideOpen ? (
          <OperationGuide
            onBack={reverseGuide}
            onClose={closeGuide}
            onNext={advanceGuide}
            spotlight={guideSpotlight}
            step={activeGuideStep}
            stepCount={GUIDE_STEPS.length}
            stepIndex={guideStepIndex}
          />
        ) : null}

        <section className="heroBand">
          <div className="heroCopy">
            <div className={passed ? 'statusBadge pass' : 'statusBadge fail'}>
              {passed ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />}
              {statusText}
            </div>
            <h2>
              <span>Private liabilities,</span>
              <span>public reserve proof.</span>
            </h2>
            <p>
              {fixture
                ? fixture.description
                : 'Load a liabilities CSV, generate a local proof package, then submit it for matching.'}{' '}
              Processing happens locally in this browser. Nothing gets sent to a server.
            </p>
          </div>

          <div className="reserveDial" aria-label="Reserve coverage">
            <div className="dialInner">
              <span>{coverage}</span>
              <small>coverage</small>
            </div>
          </div>
        </section>

        <section className="metricGrid" aria-label="Reserve metrics">
          <Metric
            icon={<Database size={18} />}
            label="Liabilities"
            value={fixture?.snapshot.liabilities ?? 'n/a'}
          />
          <Metric
            icon={<RadioTower size={18} />}
            label="Assets"
            value={fixture?.snapshot.assets ?? 'n/a'}
          />
          <Metric icon={<Network size={18} />} label="Network" value="Stellar testnet" />
          <Metric icon={<FileCheck2 size={18} />} label="Circuit" value="solvency v1" />
        </section>

        <section className="panel workflowPanel" id="submit">
          <div className="panelHeader">
            <div>
              <p className="eyebrow">Issuer workflow</p>
              <h3>CSV to proof package</h3>
            </div>
            <label className="fileButton" data-guide-id="load-csv">
              <Upload size={18} />
              <span>Load CSV</span>
              <input accept=".csv,text/csv" className="srOnly" onChange={loadCsvFile} type="file" />
            </label>
          </div>

          <div className="privacyStrip">
            <ServerOff size={18} />
            <span>CSV parsing, proof package generation, and matching happen in this browser.</span>
          </div>

          <div className="workflowSteps">
            <WorkflowStep
              active={Boolean(csvText.trim())}
              detail={csvFileName}
              label="1"
              title="Import liabilities CSV"
            />
            <WorkflowStep
              active={Boolean(generatedFixture)}
              detail={generatedFixture ? generatedFixture.snapshot.proof_hash : 'Ready to generate'}
              label="2"
              title="Generate local proof"
            />
            <WorkflowStep
              active={submittedGeneratedProof}
              detail={submittedGeneratedProof ? 'Verified' : 'Awaiting verification'}
              label="3"
              title="Verify proof"
            />
          </div>

          <div className="csvWorkflowGrid">
            <div className="csvEditor">
              <div className="csvToolbar">
                <span>{csvFileName}</span>
              </div>
              <textarea
                aria-label="Liabilities CSV"
                value={csvText}
                onChange={(event) => {
                  setCsvText(event.currentTarget.value);
                  setGeneratedFixture(null);
                  setLocalVerificationStatus('');
                  setOnchainVerificationStatus('');
                }}
                spellCheck={false}
              />
            </div>

            <div className="issuerForm">
              <TextInput
                label="Assets"
                value={issuerCsvForm.assets}
                onChange={(value) => updateIssuerCsvForm('assets', value)}
              />
              <TextInput
                label="Period"
                value={issuerCsvForm.period}
                onChange={(value) => updateIssuerCsvForm('period', value)}
              />
              <TextInput
                label="Proof row"
                value={issuerCsvForm.proofIndex}
                onChange={(value) => updateIssuerCsvForm('proofIndex', value)}
              />
              <TextInput
                label="Subject"
                value={issuerCsvForm.subject}
                onChange={(value) => updateIssuerCsvForm('subject', value)}
              />
              <TextInput
                label="Attestor"
                value={issuerCsvForm.attestor}
                onChange={(value) => updateIssuerCsvForm('attestor', value)}
              />
            </div>
          </div>

          {generatedFixture ? (
            <div className="generatedSummary">
              <MiniStat label="Liabilities" value={generatedFixture.snapshot.liabilities} />
              <MiniStat label="Assets" value={generatedFixture.snapshot.assets} />
              <MiniStat label="Coverage" value={formatCoverage(generatedFixture.snapshot)} />
              <MiniStat
                label="Rows"
                value={String(generatedFixture.diagnostics?.csvRows ?? 'n/a')}
              />
            </div>
          ) : null}

          <div className="submitFooter">
            <button
              className="primaryAction compact"
              data-guide-id="generate-proof"
              onClick={generateCsvProof}
              type="button"
            >
              <Cpu size={18} />
              {isGenerating ? 'Generating' : 'Generate proof'}
            </button>
            <button
              className="secondaryAction compact"
              data-guide-id="verify-local"
              disabled={!generatedFixture}
              onClick={verifyGeneratedProofLocally}
              type="button"
            >
              <ClipboardCheck size={18} />
              Verify locally
            </button>
            <button
              className="secondaryAction compact"
              data-guide-id="verify-onchain"
              disabled={!generatedFixture}
              onClick={verifyGeneratedProofOnchain}
              type="button"
            >
              <Network size={18} />
              Verify on-chain
            </button>
            <button
              className="secondaryAction compact"
              disabled={!generatedFixture}
              onClick={() =>
                generatedFixture &&
                downloadJsonAsset(
                  generatedFixture,
                  `zkreserves-${safeFileSegment(generatedFixture.id)}-proof-package.json`,
                )
              }
              type="button"
            >
              <Download size={18} />
              Download package
            </button>
            {localVerificationStatus ? (
              <span className={passed ? 'inputSuccess' : 'inputError'}>{localVerificationStatus}</span>
            ) : null}
            {onchainVerificationStatus ? (
              <span className={passed ? 'inputSuccess' : 'inputError'}>{onchainVerificationStatus}</span>
            ) : null}
            {inputError ? <span className="inputError">{inputError}</span> : null}
          </div>
        </section>

        <section className="panel submitPanel" id="advanced">
          <div className="panelHeader">
            <div>
              <p className="eyebrow">Advanced import</p>
              <h3>Raw proof JSON</h3>
            </div>
            <label className="fileButton">
              <Upload size={18} />
              <span>Load fixture</span>
              <input
                accept="application/json,.json"
                className="srOnly"
                onChange={loadFixtureFile}
                type="file"
              />
            </label>
          </div>

          <div className="privacyStrip">
            <ServerOff size={18} />
            <span>Use this for exported proof packages or developer fixtures.</span>
          </div>

          <div className="editorGrid">
            <JsonEditor
              label="Snapshot"
              value={editorText.snapshot}
              onChange={(value) => updateEditor('snapshot', value)}
            />
            <JsonEditor
              label="Report"
              value={editorText.report}
              onChange={(value) => updateEditor('report', value)}
            />
            <JsonEditor
              label="Proof"
              value={editorText.proof}
              onChange={(value) => updateEditor('proof', value)}
            />
          </div>

          <div className="submitFooter">
            <button className="primaryAction compact" onClick={verifyEditorPayload} type="button">
              <ClipboardCheck size={18} />
              Verify locally
            </button>
            {inputError ? <span className="inputError">{inputError}</span> : null}
          </div>
        </section>

        <section className="contentGrid" id="verify">
          <div className="panel verificationPanel">
            <div className="panelHeader">
              <div>
                <p className="eyebrow">Verifier</p>
                <h3>Checks</h3>
              </div>
              <ClipboardCheck size={20} />
            </div>
            <div className="checkList">
              {checks.map((check) => (
                <div className={check.passed ? 'checkRow' : 'checkRow fail'} key={check.label}>
                  {check.passed ? <CheckCircle2 size={18} /> : <AlertTriangle size={18} />}
                  <div>
                    <strong>{check.label}</strong>
                    <span>{check.detail}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="panel hashPanel">
            <div className="panelHeader">
              <div>
                <p className="eyebrow">Stellar payload</p>
                <h3>Snapshot</h3>
              </div>
              <button
                className="iconButton"
                disabled={!fixture}
                onClick={copyPayload}
                aria-label="Copy demo payload"
              >
                {copied ? <CheckCircle2 size={18} /> : <Copy size={18} />}
              </button>
            </div>
            {fixture ? <HashRows snapshot={fixture.snapshot} /> : <EmptyState label="No snapshot" />}
          </div>
        </section>

        <section className="panel commandPanel" id="publish">
          <div className="panelHeader">
            <div>
              <p className="eyebrow">On-chain publish</p>
              <h3>Stellar RPC</h3>
            </div>
            <div className="panelActions">
              <button
                className="secondaryAction compact"
                data-guide-id="load-onchain-setup"
                onClick={loadDemoOnchainSetup}
                type="button"
              >
                <FileCheck2 size={18} />
                Load demo on-chain setup
              </button>
              <RadioTower size={20} />
            </div>
          </div>
          <div className="privacyStrip">
            <Terminal size={18} />
            <span>
              Publishing uses your connected browser wallet. The app prepares the transaction, your
              wallet signs it, and the signed XDR is submitted through Stellar RPC.
            </span>
          </div>

          <div className="generatedSummary">
            <MiniStat label="Connected wallet" value={walletAddress || 'Not connected'} />
            <MiniStat label="Network" value="Testnet" />
            <MiniStat label="Signer" value="Browser wallet" />
            <MiniStat
              label="Status"
              value={fixture && walletAddress ? 'Ready after artifacts' : 'Needs proof or wallet'}
            />
          </div>

          <div className="rpcForm">
            <TextInput
              label="RPC URL"
              value={rpcForm.rpcUrl}
              onChange={(value) => updateRpcForm('rpcUrl', value)}
            />
            <TextInput
              label="Registry contract"
              value={rpcForm.registryContractId}
              onChange={(value) => updateRpcForm('registryContractId', value)}
            />
            <TextInput
              label="Verifier contract"
              value={rpcForm.verifierContractId}
              onChange={(value) => updateRpcForm('verifierContractId', value)}
            />
            <TextInput
              label="Subject address"
              value={rpcForm.subjectAddress}
              onChange={(value) => updateRpcForm('subjectAddress', value)}
            />
          </div>

          <div className="editorGrid rpcArtifacts">
            <JsonEditor
              label="Verification key JSON"
              value={rpcForm.verificationKeyJson}
              onChange={(value) => updateRpcForm('verificationKeyJson', value)}
            />
            <JsonEditor
              label="Proof JSON"
              value={rpcForm.proofJson}
              onChange={(value) => updateRpcForm('proofJson', value)}
            />
            <JsonEditor
              label="Public signals JSON"
              value={rpcForm.publicSignalsJson}
              onChange={(value) => updateRpcForm('publicSignalsJson', value)}
            />
          </div>

          <div className="submitFooter">
            <button
              className="primaryAction compact"
              data-guide-id="publish-rpc"
              disabled={!fixture || isPublishingRpc || !walletAddress}
              onClick={publishViaRpc}
              type="button"
            >
              <Network size={18} />
              {isPublishingRpc ? 'Publishing' : 'Publish via RPC'}
            </button>
            {rpcStatus ? (
              <span className="inputSuccess">
                {rpcStatus}
                {rpcTransactionHash ? (
                  <>
                    {': '}
                    <a
                      href={stellarTestnetTransactionUrl(rpcTransactionHash)}
                      rel="noreferrer"
                      target="_blank"
                    >
                      {rpcTransactionHash}
                    </a>
                  </>
                ) : null}
              </span>
            ) : null}
            {rpcError ? <span className="inputError">{rpcError}</span> : null}
          </div>
        </section>
      </section>
    </main>
  );
}

function OperationGuide({
  onBack,
  onClose,
  onNext,
  spotlight,
  step,
  stepCount,
  stepIndex,
}: {
  onBack: () => void;
  onClose: () => void;
  onNext: () => void;
  spotlight: GuideSpotlight | null;
  step: GuideStepDefinition;
  stepCount: number;
  stepIndex: number;
}) {
  const isLastStep = stepIndex === stepCount - 1;

  return (
    <div className="guideOverlay" role="presentation">
      {spotlight ? <div className="guideHalo" style={guideHaloStyle(spotlight)} /> : null}
      <section
        aria-labelledby="guide-title"
        className="guideTooltip"
        role="dialog"
        style={guideTooltipStyle(spotlight)}
      >
        <div className="guideHeader">
          <div>
            <p className="eyebrow">
              Step {stepIndex + 1} of {stepCount}
            </p>
            <h3 id="guide-title">{step.title}</h3>
          </div>
          <button className="iconButton" onClick={onClose} type="button" aria-label="Close guide">
            <X size={18} />
          </button>
        </div>

        <p>{step.detail}</p>
        <div className="guideHint">{step.hint}</div>

        <div className="guideProgress" aria-hidden="true">
          {Array.from({ length: stepCount }, (_, index) => (
            <span className={index === stepIndex ? 'active' : ''} key={index} />
          ))}
        </div>

        <div className="guideFooter">
          <button
            className="secondaryAction compact"
            disabled={stepIndex === 0}
            onClick={onBack}
            type="button"
          >
            Back
          </button>
          <button className="primaryAction compact" onClick={onNext} type="button">
            {isLastStep ? 'Finish' : 'Next'}
          </button>
        </div>
      </section>
    </div>
  );
}

function guideHaloStyle(spotlight: GuideSpotlight): React.CSSProperties {
  return {
    height: spotlight.height,
    left: spotlight.left,
    top: spotlight.top,
    width: spotlight.width,
  };
}

function guideTooltipStyle(spotlight: GuideSpotlight | null): React.CSSProperties {
  const width = Math.min(380, window.innerWidth - 24);
  if (!spotlight) {
    return {
      left: Math.max(12, (window.innerWidth - width) / 2),
      top: 96,
      width,
    };
  }

  const gap = 18;
  const left = Math.min(
    Math.max(12, spotlight.left + spotlight.width / 2 - width / 2),
    window.innerWidth - width - 12,
  );
  const below = spotlight.top + spotlight.height + gap;

  if (below + 260 < window.innerHeight) {
    return { left, top: below, width };
  }

  return {
    bottom: Math.max(12, window.innerHeight - spotlight.top + gap),
    left,
    width,
  };
}

function Feature({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <article className="feature">
      <div className="metricIcon">{icon}</div>
      <h3>{title}</h3>
      <p>{children}</p>
    </article>
  );
}

function WorkflowStep({
  active,
  detail,
  label,
  title,
}: {
  active: boolean;
  detail: string;
  label: string;
  title: string;
}) {
  return (
    <div className={active ? 'workflowStep active' : 'workflowStep'}>
      <strong>{label}</strong>
      <div>
        <span>{title}</span>
        <code>{detail}</code>
      </div>
    </div>
  );
}

function MiniStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="miniStat">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function TextInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="textInput">
      <span>{label}</span>
      <input value={value} onChange={(event) => onChange(event.currentTarget.value)} />
    </label>
  );
}

function JsonEditor({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="jsonEditor">
      <span>{label}</span>
      <textarea value={value} onChange={(event) => onChange(event.currentTarget.value)} />
    </label>
  );
}

function Metric({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="metric">
      <div className="metricIcon">{icon}</div>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function HashRows({ snapshot }: { snapshot: Snapshot }) {
  return (
    <div className="hashRows">
      {Object.entries(snapshot).map(([key, value]) => (
        <div className="hashRow" key={key}>
          <span>{key}</span>
          <code>{value}</code>
        </div>
      ))}
    </div>
  );
}

function EmptyState({ label }: { label: string }) {
  return <div className="emptyState">{label}</div>;
}

function formatCoverage(snapshot: Snapshot): string {
  try {
    const assets = BigInt(snapshot.assets);
    const liabilities = BigInt(snapshot.liabilities);
    if (liabilities === 0n) {
      return assets === 0n ? '1.00x' : 'open';
    }
    return (Number(assets) / Number(liabilities)).toFixed(2) + 'x';
  } catch {
    return 'n/a';
  }
}

function shortAddress(address: string): string {
  return `${address.slice(0, 5)}...${address.slice(-4)}`;
}

async function signWalletTransaction(
  transactionXdr: string,
  opts: { address: string; networkPassphrase: string },
): Promise<string> {
  const result = await signTransaction(transactionXdr, opts);
  assertFreighterResult(result, 'Unable to sign transaction with browser wallet');

  if (!result.signedTxXdr) {
    throw new Error('Browser wallet did not return a signed transaction');
  }
  if (result.signerAddress && result.signerAddress !== opts.address) {
    throw new Error('Browser wallet signed with a different account than the connected wallet');
  }

  return result.signedTxXdr;
}

function assertFreighterResult<T extends { error?: { message?: string } }>(
  result: T,
  fallback: string,
): asserts result is T & { error?: undefined } {
  if (result.error) {
    throw new Error(result.error.message || fallback);
  }
}

function stellarTestnetTransactionUrl(hash: string): string {
  return `${STELLAR_TESTNET_TX_BASE_URL}${hash}`;
}

function downloadJsonAsset(payload: unknown, filename: string) {
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
  downloadBlob(blob, filename);
}

function downloadTextAsset(payload: string, filename: string, type: string) {
  downloadBlob(new Blob([payload], { type }), filename);
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function safeFileSegment(value: string): string {
  const segment = value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  return segment || 'fixture';
}

createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
